import { NotificationTypes, showNotification } from "store/notifications/slice";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import i18n from "i18next";

type HandleApiErrorParams = {
  error: unknown;
  t: (key: string) => string;
  dispatch: any;
  formErrors?: {
    setFieldError: (field: string, code: string) => void;
  };
};

const normalizeErrorCode = (code: string) => {
  if (typeof code !== "string") return code;
  return code.replace(/^"+|"+$/g, "");
};

const humanizeErrorCode = (code: string) => {
  const readable = normalizeErrorCode(code)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();

  return readable
    ? `${readable.charAt(0).toUpperCase()}${readable.slice(1)}`
    : code;
};

const getErrorMessage = (code: string, t: (key: string) => string) => {
  const normalizedCode = normalizeErrorCode(code);
  const errorKey = `errors.${normalizedCode}`;

  if (i18n.exists(errorKey)) return t(errorKey);
  if (i18n.exists(normalizedCode)) return t(normalizedCode);

  return humanizeErrorCode(normalizedCode);
};

const getResponseData = (error: unknown) => {
  const anyError = error as {
    response?: { data?: unknown };
    data?: unknown;
  };

  return anyError?.response?.data ?? anyError?.data;
};

const isApiErrorPayload = (value: unknown): value is ApiErrorPayload => {
  if (!value || typeof value !== "object") return false;

  const payload = value as Partial<ApiErrorPayload>;

  return (
    Array.isArray(payload.fieldErrors) || Array.isArray(payload.globalErrors)
  );
};

const getApiError = (error: unknown): ApiErrorPayload | null => {
  const responseData = getResponseData(error) as
    | {
        error?: unknown;
        errors?: { message: string }[];
        message?: string;
      }
    | undefined;

  if (responseData && isApiErrorPayload(responseData.error)) {
    return responseData.error;
  }

  if (isApiErrorPayload(responseData)) {
    return responseData;
  }

  if (responseData && Array.isArray(responseData.errors)) {
    return {
      code: "BusinessError",
      fieldErrors: [],
      globalErrors: responseData.errors.map((item) => ({
        code: normalizeErrorCode(item.message),
      })),
    };
  }

  if (responseData && typeof responseData.message === "string") {
    return {
      code: "BusinessError",
      fieldErrors: [],
      globalErrors: [{ code: normalizeErrorCode(responseData.message) }],
    };
  }

  if (isApiErrorPayload(error)) {
    return error;
  }

  return null;
};

export const getBusinessErrorCodes = (error: unknown): string[] => {
  const apiError = getApiError(error);

  if (!apiError?.globalErrors?.length) {
    return [];
  }

  return apiError.globalErrors.map(({ code }) => normalizeErrorCode(code));
};

export const getBusinessErrorMessage = (
  error: unknown,
  t: (key: string) => string,
  fallbackKey: string,
) => {
  const code = getBusinessErrorCodes(error)[0];
  if (!code) return t(fallbackKey);

  const key = `errors.${code}`;
  return i18n.exists(key) ? t(key) : t(fallbackKey);
};

export const handleApiError = ({
  error,
  t,
  dispatch,
  formErrors,
}: HandleApiErrorParams) => {
  const apiError = getApiError(error);

  if (!apiError) {
    dispatch(
      showNotification({
        title: t("error"),
        message: t("unknownError"),
        type: NotificationTypes.error,
      }),
    );
    return;
  }

  if (
    Array.isArray(apiError.fieldErrors) &&
    apiError.fieldErrors.length &&
    formErrors
  ) {
    apiError.fieldErrors.forEach(({ field, code }) => {
      formErrors.setFieldError(field, normalizeErrorCode(code));
    });
    if (!apiError.globalErrors?.length) return;
  }

  if (Array.isArray(apiError.globalErrors) && apiError.globalErrors.length) {
    apiError.globalErrors.forEach(({ code }) => {
      dispatch(
        showNotification({
          title: t("error"),
          message: getErrorMessage(code, t),
          type: NotificationTypes.error,
        }),
      );
    });
    return;
  }

  dispatch(
    showNotification({
      title: t("error"),
      message: t("unknownError"),
      type: NotificationTypes.error,
    }),
  );
};
